import { get, last } from 'lodash';
import {
    getGetConfig,
    getPostConfig,
    makeApiGetRequest,
    makeApiPatchRequest,
    makeApiPatchRetryRequest,
    makeAPIRetryableRequest,
    makeAPIRequestRetryErrors,
} from '../api/apiActions';
import { AR_TRO_RETRY_ATTEMPTS } from '../../constants/http';
import { RESULT_NOT_FOUND_ERROR, VR_RESULT_STATUS, AR_TRO_RETRIABLE_ERRORS } from '../../constants/error';
import {
    WAITING_SOAPBOX_RETRY_DELAY_MS,
    WAITING_SOAPBOX_RETRY_DELAY_THRESHOLD_MS
} from '../../constants/time';
import selectors from '../../rootSelectors';

const { PENDING } = VR_RESULT_STATUS;

export const getDelay = (prevDelay = WAITING_SOAPBOX_RETRY_DELAY_MS) => {
    const newDelay = prevDelay - WAITING_SOAPBOX_RETRY_DELAY_THRESHOLD_MS;
    return newDelay < WAITING_SOAPBOX_RETRY_DELAY_THRESHOLD_MS ? WAITING_SOAPBOX_RETRY_DELAY_THRESHOLD_MS : newDelay;
};

class TROServiceClient {
    static NAME = 'tro';

    static TRO_CONTENT_URL = '/tro_content_api/books';

    static TRO_RESULTS_URL = '/tro_results_api';

    static RESULT_OPERATION = {
        ADD_BOOK_STATS: 'add_book_stats',
        ADD_BOOK_RESULT: 'add_book_result',
        ADD_COMPREHENSION_RESPONSE: 'add_comprehension_response',
        DISCONTINUE: 'discontinue'
    };

    /**
     * Construct and return the URL API endpoint for a list of results
     */
    static constructResultListEndpoint({
        studentId, grade, period, schoolYear, path
    } = {}) {
        let endpoint = `${path}/results`;
        const params = [];
        if (studentId) {
            params.push(`student_uids=${studentId}`);
        }
        if (grade) {
            params.push(`grade_level=${grade}`);
        }
        if (period) {
            params.push(`period=${period}`);
        }
        if (schoolYear) {
            params.push(`school_year=${schoolYear}`);
        }
        if (params.length > 0) {
            endpoint += `?${params.join('&')}`;
        }
        return endpoint;
    }

    /**
     * Construct and return the URL API endpoint for a given resultId
     * @param {str} resultId
     */
    static constructResultEndpoint(resultId) {
        return `${TROServiceClient.TRO_RESULTS_URL}/results/${resultId}`;
    }

    /**
     * Construct and return the URL API endpoint for books entity
     * @param {object} config - contains the parameters necessary for the book content endpoint
     * @property {string} id - the book id of the book wanted
     * @property {string} gradeLevel - the grade level of the active student account
     * @property {string} period - the current TOY period
     * @property {string} relativeLevel - the level book the student should be assessed on
     */
    static constructBookEndpoint(config) {
        let endpoint = `${TROServiceClient.TRO_CONTENT_URL}`;
        if (config.id) {
            const { id } = config;
            endpoint += `/${id}`;
        }
        else if (config.gradeLevel && config.period && config.relativeLevel) {
            const { gradeLevel, period, relativeLevel } = config;
            endpoint += `/${gradeLevel}/${period}/${relativeLevel}`;
        }
        return endpoint;
    }

    /**
     * Sends PATCH request for result endpoint and returns a response
     * @param {str} resultId - id of the result to PATCH
     * @param {str} operation - PATCH operation, one of TROServiceClient.RESULT_OPERATION
     * @param {object} data - PATCH data specific for provided operation
     * @param {object} studentUid - Student UID of the user
     * @param {object} studentUid - Current Inst UID of the user. Only used for add_comprehension_response and add_book_result ops.
     * @returns {JSON} server response
     */
    static patchResult = (resultId, operation, data, studentUid, instId = null) => (dispatch, getStore) => {
        const patchData = {
            operation,
            data,
            student_uid: studentUid,
            ...(instId && {inst_uid: instId})
        };
        const endpoint = TROServiceClient.constructResultEndpoint(resultId);
        const shouldRetry = selectors.getExternalURLSourceStatus(getStore());

        return shouldRetry
            ? dispatch(makeApiPatchRetryRequest(endpoint, patchData, AR_TRO_RETRIABLE_ERRORS, AR_TRO_RETRY_ATTEMPTS))
            : dispatch(makeApiPatchRequest(endpoint, patchData));
    };

    /**
     * Sends GET request to results endpoint with provided parameters
     * @param {str} studentId - id of the student to get results for
     * @param {str} grade - (optional) student's grade
     * @param {str} period - (optional) BOY | MOY | EOY
     * @param {number} schoolYear - assessment school year
     * @returns {JSON} server response
     */
    static getResults = (studentId, grade, period, schoolYear) => dispatch => {
        const config = {
            studentId,
            grade,
            period,
            schoolYear,
            path: TROServiceClient.TRO_RESULTS_URL,
        };
        const endpoint = TROServiceClient.constructResultListEndpoint(config);
        return dispatch(makeApiGetRequest(endpoint, true));
    };

    /**
     * Sends GET retrying request to results with endpoint with provided parameters
     * Retry if vr result status of last book result is pending or failed
     * with error_reason timeout, otherwise no retry.
     * @param {str} studentId - id of the student to get results for
     * @param {str} grade - (optional) student's grade
     * @param {str} period - (optional) BOY | MOY | EOY
     * @param {number} schoolYear - assessment school year
     * @param {str} instId - id of the institution that the student is a part of
     * @param {str} resultId - id of the result
     * @returns {Promise} promise which resolves when result with ready or timeout
     *                    status is received or number of retries is reached,
     *                    rejects on api errors
     */
    static getReadyResultWithRetry = (studentId, grade, period, schoolYear, resultId) => (dispatch, getStore) => {
        const retryCondition = response => {
            const currentResult = response.find(result => result.result_id === resultId);

            if (!currentResult) Promise.reject(RESULT_NOT_FOUND_ERROR);

            const shouldRetry = selectors.getIsBackgroundRetryInProgress(getStore());
            const bookResult = last(get(currentResult, 'measure_result.book_results', {}));
            const vrResultStatus = get(bookResult, 'vrr_result.status');

            return shouldRetry && vrResultStatus === PENDING;
        };

        const config = {
            studentId,
            grade,
            period,
            schoolYear,
            path: TROServiceClient.TRO_RESULTS_URL,
        };

        const endpoint = TROServiceClient.constructResultListEndpoint(config);
        return dispatch(makeAPIRetryableRequest(
            endpoint,
            getGetConfig(),
            retryCondition,
            10, // should be more 9 as the retrying will last 2 minutes
            WAITING_SOAPBOX_RETRY_DELAY_MS,
            getDelay,
        ));
    };

    /**
     * Construct request body and send POST request with student audio recording
     * @param {str} studentUid - student uid
     * @param {str} bookText - book text the student read
     * @param {str} encodedWav - base64 encoded .wav file
     * @param {int} firstRecordedWordIndex - index of the first word that was recorded relative to the whole passage
     * @param {str} resultId - result id
     * @param {str} bookId - book id
     * @param {number} lastWordIndex - Index of the last word on the last recorded page relative to the passage
     * @returns {JSON} server response
     */
    static postRecording = ({
        studentUid,
        encodedWav,
        resultId,
        bookId,
        lastWordIndex
    }) => dispatch => {
        const endpoint = `${TROServiceClient.TRO_RESULTS_URL}/results/${resultId}/${bookId}/student_voice_recording`;
        const data = {
            student_uid: studentUid,
            encoded_wav: encodedWav,
            last_word_index: lastWordIndex
        };
        const config = getPostConfig(data);

        return dispatch(makeAPIRequestRetryErrors(endpoint, config));
    };

    /**
     * POST request to verify microphone output is acceptable for SoapBox analysis
     * @param {str} encodedWav - base64-encoded wav file
     * @param {str} target - target phrase which should match recorded audio
     * @param {object} studentDetails - student details to be used for building path to object in S3
     * @returns {JSON} server response
     */
    static postVerification = ({
        encodedWav,
        target,
        studentDetails,
        verificationType
    }) => dispatch => {
        const endpoint = `${TROServiceClient.TRO_RESULTS_URL}/verification`;
        const data = {
            encoded_wav: encodedWav,
            target,
            student_details: studentDetails,
            verification_type: verificationType,
        };

        const config = getPostConfig(data);

        return dispatch(makeAPIRequestRetryErrors(endpoint, config));
    }
}

export default TROServiceClient;
